import { Suspense } from 'react'
import { lazyWithReload, LoadingState } from '@bas/shared'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { App } from './App'
import { CC, ccPath } from './basePath'

// ─── Lazy-loaded page components ─────────────────────────────────────────────

const Dashboard             = lazyWithReload(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Leads                 = lazyWithReload(() => import('./pages/Leads').then((m) => ({ default: m.Leads })))
const CustomerDetail        = lazyWithReload(() => import('./pages/CustomerDetail').then((m) => ({ default: m.CustomerDetail })))
const Documents             = lazyWithReload(() => import('./pages/Documents').then((m) => ({ default: m.Documents })))
const EmailInbox            = lazyWithReload(() => import('./pages/EmailInbox').then((m) => ({ default: m.EmailInbox })))
const SmsInbox              = lazyWithReload(() => import('./pages/SmsInbox').then((m) => ({ default: m.SmsInbox })))
const Approvals             = lazyWithReload(() => import('./pages/Approvals').then((m) => ({ default: m.Approvals })))
const Agents                = lazyWithReload(() => import('./pages/Agents').then((m) => ({ default: m.Agents })))
const BusinessProfile       = lazyWithReload(() => import('./pages/BusinessProfile').then((m) => ({ default: m.BusinessProfile })))
const Scheduling            = lazyWithReload(() => import('./pages/Scheduling').then((m) => ({ default: m.Scheduling })))
const Activity              = lazyWithReload(() => import('./pages/Activity').then((m) => ({ default: m.Activity })))
const Prospecting           = lazyWithReload(() => import('./pages/Prospecting').then((m) => ({ default: m.Prospecting })))
const Reports               = lazyWithReload(() => import('./pages/Reports').then((m) => ({ default: m.Reports })))
const ReferAndEarn          = lazyWithReload(() => import('./pages/ReferAndEarn').then((m) => ({ default: m.ReferAndEarn })))
const Settings              = lazyWithReload(() => import('./pages/Settings').then((m) => ({ default: m.Settings })))
const Setup                 = lazyWithReload(() => import('./pages/Setup').then((m) => ({ default: m.Setup })))
const MobileApprove         = lazyWithReload(() => import('./pages/MobileApprove').then((m) => ({ default: m.MobileApprove })))
const MobileProfileQuestion = lazyWithReload(() => import('./pages/MobileProfileQuestion').then((m) => ({ default: m.MobileProfileQuestion })))
const MobilePickDesign      = lazyWithReload(() => import('./pages/MobilePickDesign').then((m) => ({ default: m.MobilePickDesign })))

// ─── Loading fallback ─────────────────────────────────────────────────────────

function PageLoader() {
  return <LoadingState label="Loading this page…" className="min-h-[60vh]" />
}

// ─── Router ───────────────────────────────────────────────────────────────────

export const router = createBrowserRouter(
  [
    {
      // Base-driven: '/control-center' on the Firebase build, '/' on the
      // app.justtextava.com root build. See basePath.ts.
      path: CC || '/',
      element: <App />,
      children: [
        // Default redirect
        { index: true, element: <Navigate to={ccPath('/dashboard')} replace /> },

        {
          path: 'dashboard',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Dashboard />
            </Suspense>
          ),
        },
        {
          // Canonical post-2026-05-26 path: /control-center/customers.
          // The page component is still named Leads (file rename happens in
          // the Customer Detail extension task); behavior unchanged here.
          path: 'customers',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Leads />
            </Suspense>
          ),
        },
        {
          path: 'email',
          element: (
            <Suspense fallback={<PageLoader />}>
              <EmailInbox />
            </Suspense>
          ),
        },
        {
          // Texts — every SMS a customer sends the business. There was an Email inbox and
          // no SMS equivalent, so a customer's text had nowhere to land even once we
          // started storing it. See receptionist/smsReceptionist.ts.
          path: 'texts',
          element: (
            <Suspense fallback={<PageLoader />}>
              <SmsInbox />
            </Suspense>
          ),
        },
        {
          // ⚠️ CALLS FOLDED UNDER ACTIVITY (CC review #2, 2026-09-22) — pure history,
          // nothing to act on, and Activity now shows every call with listen /
          // transcript. The old URL keeps working as a filtered view.
          path: 'calls',
          element: <Navigate to={ccPath('/activity?view=call')} replace />,
        },
        {
          // Customer Detail standalone page (post-2026-05-26 IA — detail
          // views are their own pages, never inline `<tr>` expansions).
          // See feedback_detail_pages_not_inline_expansions.md.
          path: 'customers/:customerId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <CustomerDetail />
            </Suspense>
          ),
        },
        // Legacy /leads path — redirects to /customers so old bookmarks and
        // any in-flight emails/SMS links keep working.
        { path: 'leads', element: <Navigate to={ccPath('/customers')} replace /> },
        {
          path: 'documents',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Documents />
            </Suspense>
          ),
        },
        {
          path: 'approvals',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Approvals />
            </Suspense>
          ),
        },
        // ⚠️ THE PAGE IS RETIRED BUT THE ROUTE IS NOT (2026-09-20). Ava's Skills
        // left the nav once everything on it had a real home; dropping the route
        // as well is how a bookmark, a magic link or an older Ava text becomes a
        // dead end. It still renders — Billing owns buying, Settings owns
        // configuration, the Dashboard owns per-skill activity, and this is now
        // the stats view nothing links to.
        {
          path: 'agents',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Agents />
            </Suspense>
          ),
        },
        {
          path: 'business-profile',
          element: (
            <Suspense fallback={<PageLoader />}>
              <BusinessProfile />
            </Suspense>
          ),
        },
        {
          path: 'scheduling',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Scheduling />
            </Suspense>
          ),
        },
        // ⚠️ REDIRECT, NOT A PAGE (2026-09-19). Integrations lost its sidebar entry
        // when it moved into Settings, and then its own title and preamble — so
        // rendering it bare here would hand an old link a headless page. The
        // canonical home is Settings > Integrations, exactly as /connected-accounts
        // already resolves. The route stays so every existing link still works.
        { path: 'integrations', element: <Navigate to={ccPath('/settings#integrations')} replace /> },
        {
          path: 'activity',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Activity />
            </Suspense>
          ),
        },
        {
          path: 'prospecting',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Prospecting />
            </Suspense>
          ),
        },
        {
          path: 'setup',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Setup />
            </Suspense>
          ),
        },
        // Legacy /connected-accounts route — post-2026-05-26 IA the canonical
        // home for OAuth connections is Settings > Integrations. The redirect
        // stays so an old bookmark still lands somewhere sensible.
        //
        // ⚠️ `/connected-accounts-legacy` REMOVED 2026-09-13 (John's call), and
        // the page with it. Keeping a "preserved fallback" nothing linked to had
        // a real cost: every capability only IT had was unreachable, and we did
        // not notice for months. The reachable page showed a Reconnect button
        // with no onClick while the working one sat there, and the D3
        // disconnect contract — show which skills stop BEFORE confirming — could
        // not be reached by any client at all. Both now live in
        // Settings > Integrations; the modal is shared
        // (`@shared/DisconnectAccountModal`).
        { path: 'connected-accounts', element: <Navigate to={ccPath('/settings#integrations')} replace /> },
        // Support folded into Settings > My Tickets (2026-05-26 IA). Redirect
        // the standalone path so old links land on the canonical sub-tab.
        { path: 'support', element: <Navigate to={ccPath('/settings#my-tickets')} replace /> },
        {
          path: 'reports',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Reports />
            </Suspense>
          ),
        },
        {
          path: 'refer-and-earn',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ReferAndEarn />
            </Suspense>
          ),
        },
        {
          path: 'settings',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Settings />
            </Suspense>
          ),
        },

        // Catch-all
        { path: '*', element: <Navigate to={ccPath('/dashboard')} replace /> },
      ],
    },
    // ── Standalone mobile pages (no sidebar/header chrome) ──────────────
    {
      path: '/approve/:token',
      element: (
        <Suspense fallback={<PageLoader />}>
          <MobileApprove />
        </Suspense>
      ),
    },
    {
      path: '/profile-question/:token',
      element: (
        <Suspense fallback={<PageLoader />}>
          <MobileProfileQuestion />
        </Suspense>
      ),
    },
    {
      path: '/pick-design/:token',
      element: (
        <Suspense fallback={<PageLoader />}>
          <MobilePickDesign />
        </Suspense>
      ),
    },
    // No separate '/' route: on the root build the App parent above already owns
    // '/' (its index child redirects to the dashboard); a duplicate '/' here would
    // collide. On the Firebase build the SPA is only ever served under
    // /control-center/**, so root is the marketing site, not this router.
    { path: '*', element: <Navigate to={ccPath('/dashboard')} replace /> },
  ],
  {
    basename: '/',
  }
)
